import axios, { AxiosRequestConfig } from "axios"
import { msalInstance } from "@/config/msalInstance"
import { loginRequest } from "@/config/authConfig"
import { getValidAccessToken } from "@/utils/accessToken"
import { AuthAxiosRequestConfig } from "@/utils/authAxios"
import { IS_TAZ } from "@/utils/brand"

export const SESSION_EXPIRED_ERROR = "SESSION_EXPIRED" as const

export type SessionExpiredError = Error & { code: typeof SESSION_EXPIRED_ERROR; isAuthError: true }

export const createSessionExpiredError = (): SessionExpiredError =>
    Object.assign(new Error("Session expired"), {
        code: SESSION_EXPIRED_ERROR,
        isAuthError: true as const,
    })

export const isSessionExpiredError = (error: unknown): error is SessionExpiredError =>
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    (error as { code?: string }).code === SESSION_EXPIRED_ERROR

/** After a retried request, only force sign-out for likely session/token 401s (not issuer/audience misconfig). */
export const shouldForceReauthOn401 = (error: unknown): boolean => {
    if (!axios.isAxiosError(error) || error.response?.status !== 401) {
        return false
    }
    const detail = String((error.response?.data as { detail?: string } | undefined)?.detail ?? "").toLowerCase()
    if (!detail) {
        return true
    }
    if (detail.includes("invalid token issuer") || detail.includes("invalid token audience")) {
        return false
    }
    return true
}

let sharedTokenRefresh: Promise<string | null> | null = null
let lastReauthentication: Promise<boolean> | null = null
let lastReauthenticationAt = 0
const REAUTH_DEDUP_MS = 60_000

/** Deduplicate concurrent silent refresh attempts (e.g. parallel 401s on page load). */
export const refreshAccessTokenOnce = (): Promise<string | null> => {
    sharedTokenRefresh ??= getValidAccessToken({ forceRefresh: true }).finally(() => {
        sharedTokenRefresh = null
    })
    return sharedTokenRefresh
}

export const setRequestAuthorization = (config: AxiosRequestConfig, token: string): void => {
    const authorization = `Bearer ${token}`
    if (!config.headers) {
        config.headers = { Authorization: authorization }
        return
    }
    const headers = config.headers as { set?: (name: string, value: string) => void; Authorization?: string }
    if (typeof headers.set === "function") {
        headers.set("Authorization", authorization)
        return
    }
    headers.Authorization = authorization
}

const runForceLogoutAndReauthenticate = async (): Promise<void> => {
    // TAZ (Stadt Zürich): re-authenticate WITHOUT wiping the MSAL cache or ending
    // the Entra SSO session. When the SSO session is still alive the redirect
    // returns the user seamlessly (no credential re-entry); only a truly expired
    // session prompts for sign-in. This avoids the "log in completely from
    // scratch" behaviour reported on long-lived TAZ sessions. B2C envs keep the
    // destructive logout path below (intentional for B2C custom-policy quirks).
    if (IS_TAZ) {
        const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null
        await msalInstance.acquireTokenRedirect({
            ...loginRequest,
            ...(account ? { account } : {}),
        })
        return
    }

    const allAccounts = msalInstance.getAllAccounts()
    for (const account of allAccounts) {
        try {
            await msalInstance.logout({ account })
        } catch {
            // Continue clearing cache even if logout fails.
        }
        try {
            await msalInstance.clearCache({ account })
        } catch {
            // Best-effort per-account cache clear.
        }
    }
    try {
        await msalInstance.clearCache()
    } catch {
        // Best-effort global cache clear.
    }
    msalInstance.setActiveAccount(null)
    await msalInstance.loginRedirect(loginRequest)
}

/**
 * Clear MSAL state and start a fresh interactive sign-in.
 * Deduplicated so parallel 401s do not trigger multiple redirects.
 * @returns true if loginRedirect was invoked without throwing.
 */
export const forceLogoutAndReauthenticate = (): Promise<boolean> => {
    const now = Date.now()
    if (lastReauthentication && now - lastReauthenticationAt < REAUTH_DEDUP_MS) {
        return lastReauthentication
    }

    lastReauthenticationAt = now
    lastReauthentication = runForceLogoutAndReauthenticate()
        .then(() => true)
        .catch(() => false)

    return lastReauthentication
}

/** @deprecated Prefer forceLogoutAndReauthenticate after silent refresh is exhausted. */
export const triggerReAuthentication = async (): Promise<void> => {
    await forceLogoutAndReauthenticate()
}

export type UnauthorizedRecoveryResult =
    | { action: "retry"; request: () => ReturnType<typeof axios.request> }
    | { action: "redirect"; redirectStarted: boolean }

export type ResolveAccessTokenResult =
    | { outcome: "token"; token: string }
    | { outcome: "redirect" }
    | { outcome: "failed" }

/**
 * Obtain a bearer token or start interactive re-authentication (redirect).
 */
export const resolveAccessTokenOrReauthenticate = async (options: {
    isMobileDevice: boolean
    forceRefresh?: boolean
}): Promise<ResolveAccessTokenResult> => {
    if (options.isMobileDevice) {
        window.location.href = "/mobile-oops"
        return { outcome: "redirect" }
    }

    const token = options.forceRefresh
        ? await refreshAccessTokenOnce()
        : await getValidAccessToken({ forceRefresh: false })

    if (token) {
        return { outcome: "token", token }
    }

    const redirectStarted = await forceLogoutAndReauthenticate()
    return redirectStarted ? { outcome: "redirect" } : { outcome: "failed" }
}

/**
 * Recover from a 401 API response: silent refresh once, retry with the new token on the request config.
 */
export const recoverFromUnauthorized = async (
    originalRequest: AuthAxiosRequestConfig,
    options: { isMobileDevice: boolean }
): Promise<UnauthorizedRecoveryResult> => {
    if (options.isMobileDevice) {
        window.location.href = "/mobile-oops"
        return { action: "redirect", redirectStarted: false }
    }

    const token = await refreshAccessTokenOnce()
    if (token) {
        originalRequest._authRecoveryToken = token
        originalRequest._retry = true
        return {
            action: "retry",
            request: () => axios.request(originalRequest),
        }
    }

    const redirectStarted = await forceLogoutAndReauthenticate()
    return { action: "redirect", redirectStarted }
}

/**
 * Called when silent refresh and a retried request still return 401.
 * @returns true if loginRedirect was started successfully.
 */
export const handleExhaustedUnauthorized = async (options: { isMobileDevice: boolean }): Promise<boolean> => {
    if (options.isMobileDevice) {
        window.location.href = "/mobile-oops"
        return false
    }
    return forceLogoutAndReauthenticate()
}

/** @internal Test-only reset for module-level dedup state. */
export const resetAuthSessionRecoveryForTests = (): void => {
    sharedTokenRefresh = null
    lastReauthentication = null
    lastReauthenticationAt = 0
}
